import { Record, getId } from '@akin-home/records';
import { v4 as uuid } from 'uuid';

export const generateId = (): string => uuid();

export const filterByID =
  (id: string | number) =>
  (record: Record): boolean =>
    record.id === id;

export const filterExcludeByID =
  (id: string) =>
  (record: Record): boolean =>
    record.id !== id;

export const filterExcludeByIDs =
  (ids: string[]) =>
  (record: Record): boolean =>
    !ids.includes(record.id);

export const filterIncludedInIDs =
  (ids: string[]) =>
  (record: Record): boolean =>
    ids.includes(record.id);

export const isDraftRecord = (record: Record): boolean => record.id === '';

export const JSONParseSafe = (o?: string | null, fallback?: any): any =>
  o ? JSON.parse(o) : fallback;

export const JSONStringifySafe = (o?: any, fallback?: any): string =>
  o ? JSON.stringify(o) : fallback;

export const updateRecordField =
  <T, Key extends keyof T>(field: Key) =>
  (rec: T, value: T[Key]): T => ({ ...rec, [field]: value });

/** Gets the difference between 2 recordsets.
 * Useful for determining which entries need to be created or deleted server-side. */
export const getRecordSetDifference = <T extends Record>(
  originalSet: T[],
  newSet: T[]
) => {
  const newSetIDs = newSet.map(getId);
  const originalSetIDs = originalSet.map(getId);
  const added = newSet.filter((r) => !originalSetIDs.includes(r.id));
  const removed = originalSet.filter((r) => !newSetIDs.includes(r.id));

  const updated = newSet.filter(
    (i) =>
      originalSetIDs.includes(i.id) &&
      JSON.stringify(i) !==
        JSON.stringify(originalSet.find((r) => r.id === i.id))
  );

  return { added, removed, updated };
};

/** Removes record from a list by its ID */
export const removeRecordByID = <T extends Record>(
  records: T[],
  id: string
): T[] => records.filter((r) => r.id !== id);
